import i18next from 'i18next';
import { cast } from 'mobx-state-tree';
import { NavigateFunction } from 'react-router-dom';

import { getOverAllPl, getGoalReached, returnTypedThis } from '@trader/utils';
import { defaultSubCategoryId, LOCAL_STORAGE_KEYS } from '@trader/constants';
import { localStorageService, SmartLookService } from '@trader/services';
import {
  api,
  IMakeDepositBody,
  ITransactionsBE,
  ITransferParamsBE,
  ITransfersHistoryBE,
  IUserPortfolio,
  IGetPspsParams,
  IPspsResponseData,
  IPurchasesHistoryBE,
} from '@trader/api';

import { getRootInstance } from '../configureStore/configureStore';
import { TChallengeModel, TUserStore } from './index';
import { createThunk } from '../utils/asyncModel';

export const getPortfolioDemoAsync = createThunk<
  string | undefined,
  IUserPortfolio
>(
  platformLogin =>
    async function getPortfolio(
      this: unknown,
      _options,
      _flow
    ): Promise<IUserPortfolio> {
      const response = await api.User.getUserPortfolio(platformLogin);
      const that = returnTypedThis<TUserStore>(this);

      if (!platformLogin && response) {
        that.runInAction(() => {
          that.portfolio = response;
        });
      }

      return response;
    }
);

export const getProfileDemoAsync = createThunk<void, void>(
  () =>
    async function getProfile(this: unknown, _options, _flow): Promise<void> {
      const root = getRootInstance();
      const that = returnTypedThis<TUserStore>(this);
      const response = await api.User.getUserProfile(_options);

      SmartLookService.track('Login', {
        userId: root.auth.tokens.cognitoId,
        email: response?.email || '',
        name: `${response?.firstName || ''} ${response?.lastName || ''}`,
      });

      SmartLookService.identify({
        userId: root.auth.tokens.cognitoId,
        email: response?.email || '',
        name: `${response?.firstName || ''} ${response?.lastName || ''}`,
      });

      that.runInAction(() => {
        that.profile = {
          ...response,
          isDepositAllowed: Boolean(response?.isDepositAllowed),
          isAcceptedAccountApproved: Boolean(
            response?.isAcceptedAccountApproved
          ),
        };
      });
    }
);

export const getChallengesAsync = createThunk<void, void>(
  () =>
    async function getChallenges(
      this: unknown,
      _options,
      _flow
    ): Promise<void> {
      const that = returnTypedThis<TUserStore>(this);
      const response = await api.User.getUserChallenges(_options);

      response?.map(async c => {
        const tradingAcc = that.tradingAccounts.find(
          a => a.tradingId === c.accountId
        );

        const portfolio = await api.User.getUserPortfolio(
          tradingAcc?.platformLogin
        );

        const challenge: TChallengeModel = {
          ...c,
          profitLoss24: portfolio.dailyPl,
          profitLossOverall: getOverAllPl(
            portfolio.balance,
            c.challengeAmount,
            portfolio.pl
          ),
          challengeAmount: c.challengeAmount || 0,
          purchaseAmount: c.purchaseAmount || 0,
          balance: portfolio.balance,
          currencySymbol: portfolio.currencySymbol,
          goalReached: getGoalReached(
            c.profitTarget,
            getOverAllPl(portfolio.balance, c.challengeAmount, portfolio.pl),
            c.challengeAmount
          ),
        };

        const storeChallenge: TChallengeModel | undefined =
          that.challenges.find((ch: TChallengeModel) => ch.id === challenge.id);

        that.runInAction(() => {
          if (storeChallenge) {
            const updatedChallenges: TChallengeModel[] = that.challenges.map(
              ch => {
                if (ch.id === challenge.id) {
                  ch.goalReached = challenge.goalReached;
                  ch.profitLoss24 = challenge.profitLoss24;
                  ch.profitLossOverall = challenge.profitLossOverall;
                  ch.balance = challenge.balance;
                  ch.status = challenge.status;
                  ch.challengeAmount = challenge.challengeAmount || 0;
                  ch.purchaseAmount = challenge.purchaseAmount || 0;
                  ch.balance = challenge.balance;
                  ch.remainingTime = challenge.remainingTime;
                  ch.result = challenge.result;
                  ch.payout = challenge.payout;
                  ch.minTradingDays = challenge.minTradingDays;
                  ch.tradingDays = challenge.tradingDays;
                }
                return ch;
              }
            );
            that.challenges = cast(updatedChallenges);
          } else {
            that.challenges = response.length
              ? cast([...that.challenges, challenge])
              : cast([]);
          }
        });
      });
    }
);

export const getTradingAccountsAsync = createThunk<void, void>(
  () =>
    async function getTradingAccounts(
      this: unknown,
      _options,
      _flow
    ): Promise<void> {
      const root = getRootInstance();
      const that = returnTypedThis<TUserStore>(this);
      const userId = root.auth.tokens.cognitoId;
      const tradingAccountsFromLocalStorage = localStorageService.get(
        LOCAL_STORAGE_KEYS.tradingAccounts
      );

      const response = await api.User.getTradingAccounts(_options);

      const activeAcc = (response || [])?.find(a => a.isInUse);
      const currStorageAcc =
        tradingAccountsFromLocalStorage &&
        JSON.parse(tradingAccountsFromLocalStorage)[userId];

      const isOneAccountMatched = (response || [])?.find(
        a => a.platformLogin === currStorageAcc?.platformLogin
      );

      if (currStorageAcc && isOneAccountMatched) {
        const result = (response || [])?.map(a => {
          a.isInUse = false;
          if (
            a.platformLogin === currStorageAcc?.platformLogin &&
            a.accountType === currStorageAcc?.accountType
          ) {
            a.isInUse = true;
          } else {
            // eslint-disable-next-line no-self-assign
            a.isInUse = a.isInUse;
          }
          return a;
        });
        that.runInAction(() => {
          that.tradingAccounts = cast(result);
        });
      } else {
        if (activeAcc) {
          if (tradingAccountsFromLocalStorage) {
            localStorageService.set(
              LOCAL_STORAGE_KEYS.tradingAccounts,
              JSON.stringify({
                ...JSON.parse(tradingAccountsFromLocalStorage),
                [userId]: {
                  platformLogin: activeAcc.platformLogin,
                  accountType: activeAcc.accountType,
                },
              })
            );
          } else {
            localStorageService.set(
              LOCAL_STORAGE_KEYS.tradingAccounts,
              JSON.stringify({
                [userId]: {
                  platformLogin: activeAcc.platformLogin,
                  accountType: activeAcc.accountType,
                },
              })
            );
          }
        }
        that.runInAction(() => {
          that.tradingAccounts = cast(response);
        });
      }
    }
);

interface IChangeTradingAccountAsync {
  navigation?: NavigateFunction;
  platformLogin: string;
}

export const changeTradingAccountAsync = createThunk<
  IChangeTradingAccountAsync,
  void
>(
  ({ platformLogin, navigation }) =>
    async function changeTradingAccount(
      this: unknown,
      _options,
      _flow
    ): Promise<void> {
      const root = getRootInstance();
      const that = returnTypedThis<TUserStore>(this);

      if (
        root.user.profile.scoreLevel === 'LOW' &&
        import.meta.env.VITE_BRAND === 'FrontBroker'
      ) {
        root.notifications.add({
          message: 'Score level equals low',
          options: {
            autoHideDuration: 1000,
            variant: 'warning',
          },
        });
        return;
      }

      that.runInAction(() => {
        that.tradingAccounts = cast(
          that.tradingAccounts.map(t => {
            t.isInUse = t.platformLogin === platformLogin;
            return t;
          })
        );
      });

      root.pages.trading.layout.clearLayouts();
      root.pages.trading.runInAction(() => {
        root.pages.trading.selectedCategory = null;
        root.pages.trading.selectedSubCategory = defaultSubCategoryId;
      });

      localStorageService.remove(LOCAL_STORAGE_KEYS.persist);

      const tradingAccountsFromLocalStorage = localStorageService.get(
        LOCAL_STORAGE_KEYS.tradingAccounts
      );

      const accountType = that.tradingAccounts.find(
        a => a.platformLogin === platformLogin
      )?.accountType;

      const userId = root.auth.tokens.cognitoId;

      if (tradingAccountsFromLocalStorage) {
        localStorageService.set(
          LOCAL_STORAGE_KEYS.tradingAccounts,
          JSON.stringify({
            ...JSON.parse(tradingAccountsFromLocalStorage),
            [userId]: { platformLogin, accountType },
          })
        );
      } else {
        localStorageService.set(
          LOCAL_STORAGE_KEYS.tradingAccounts,
          JSON.stringify({
            [userId]: { platformLogin, accountType },
          })
        );
      }

      const prefetch = root.pages.trading.getPrefetchInformationAsync.run({
        navigation,
        isFetchTradingAccount: false,
      });

      const positionsM =
        root.entities.positionsMetrics.getPositionsMetricsAsync.run();
      const ordersM = root.entities.ordersMetrics.getOrdersMetricsAsync.run();
      const alerts = root.entities.alerts.getAlertsAsync.run();
      const historyPositions =
        root.entities.positionsHistory.getPositionsHistoryAsync.run({
          shouldClearBeforeMerge: true,
        });
      await Promise.all([
        prefetch,
        positionsM,
        ordersM,
        alerts,
        historyPositions,
      ]);

      root.notifications.add({
        message: i18next.t('NOTIFICATIONS.ACCOUNT_HAS_BEEN_CHANGED'),
        options: {
          variant: 'success',
          autoHideDuration: 1000,
        },
      });
    }
);

export const makeDepositAsync = createThunk<IMakeDepositBody, string>(
  body =>
    async function makeDeposit(
      this: unknown,
      _options,
      _flow
    ): Promise<string> {
      const root = getRootInstance();
      const response = await api.Payment.makeDeposit(body, _options);

      SmartLookService.track('MakeDeposit', {
        userId: root.auth.tokens?.cognitoId,
        email: root.user.profile?.email,
        name: `${root.user.profile?.firstName} ${root.user.profile?.lastName}`,
        deposit: body.amount,
        tradingAccId: body.tradingAccountId || 0,
        pspId: body.pspId || 0,
      });

      return response?.returnUrl;
    }
);

export const getTransactionsAsync = createThunk<void, ITransactionsBE>(
  () =>
    async function getTransactions(
      this: unknown,
      _options,
      _flow
    ): Promise<ITransactionsBE> {
      return await api.Payment.getTransactions(_options);
    }
);
export const getPspsAsync = createThunk<IGetPspsParams, IPspsResponseData>(
  params =>
    async function getPsps(
      this: unknown,
      _options,
      _flow
    ): Promise<IPspsResponseData> {
      return await api.Payment.getPsps(params, _options);
    }
);

export const getTransfersHistoryAsync = createThunk<
  ITransferParamsBE,
  ITransfersHistoryBE
>(
  params =>
    async function getTransfersHistory(
      this: unknown,
      _options,
      _flow
    ): Promise<ITransfersHistoryBE> {
      return await api.Payment.getTransfersHistory(params, _options);
    }
);

export const getPurchaseHistoryAsync = createThunk<void, IPurchasesHistoryBE>(
  () =>
    async function getTransfersHistory(
      this: unknown,
      _options,
      _flow
    ): Promise<IPurchasesHistoryBE> {
      return await api.Payment.getPurchaseHistory({}, _options);
    }
);

export const getSettingsAsync = createThunk<void, void>(
  () =>
    async function getSettings(this: unknown, _options, _flow): Promise<void> {
      const that = returnTypedThis<TUserStore>(this);
      const response = await api.User.getUserSettings(_options);

      that.runInAction(() => {
        that.settings = {
          ...response,
          isDisplayedByUser:
            response?.isCrmTradingCentral && response?.isTradingCentral,
          isEdgeXDisplayedByUser: response?.isCrmEdgeX && response?.isEdgeX,
          isEdgeZoneDisplayedByUser:
            response?.isCrmEdgeZone && response?.isEdgeZone,
        };
      });
    }
);

interface IUserUpdateSettings {
  isTradingCentral: boolean;
  isEdgeX: boolean;
  isEdgeZone: boolean;
}

export const updateSettingsAsync = createThunk<IUserUpdateSettings, void>(
  ({ isTradingCentral, isEdgeX, isEdgeZone }) =>
    async function updateSettings(
      this: unknown,
      _options,
      _flow
    ): Promise<void> {
      const that = returnTypedThis<TUserStore>(this);
      await api.User.updateUserSettings(
        isTradingCentral,
        isEdgeX,
        isEdgeZone,
        _options
      );

      that.runInAction(() => {
        that.settings.isTradingCentral = isTradingCentral;
        that.settings.isDisplayedByUser = isTradingCentral;
        that.settings.isEdgeX = isEdgeX;
        that.settings.isEdgeXDisplayedByUser = isEdgeX;
        that.settings.isEdgeZone = isEdgeZone;
        that.settings.isEdgeZoneDisplayedByUser = isEdgeZone;
      });
    }
);

export const getUserBalanceAsync = createThunk<void, void>(
  () =>
    async function getUserBalance(
      this: unknown,
      _options,
      _flow
    ): Promise<void> {
      const that = returnTypedThis<TUserStore>(this);
      const response = await api.User.getUserBalance(_options);
      that.runInAction(() => {
        that.portfolio.balance = response.balance;
        that.portfolio.currency = response.currency;
        that.portfolio.currencySymbol = response.currencySymbol;
      });
    }
);
